// The action model (brief §8) and the dependency chain (§7.5). Production can't
// express any of this — EligibilityRuleConfig.Apply receives only facts, never
// prior results, and the configs are evaluated independently. Here the resolver
// sees the full award picture (both facts and simulated awards), which is the
// signature change production would need. Noted in the changelog-equivalent.

import { M } from '@/benefits-flow/engine/metrics';
import { B } from '@/benefits-flow/engine/rules';
import { BENEFITS } from '@/benefits-flow/engine/benefits';
import type {
  ActionState,
  EligibilityStatus,
  Facts,
} from '@/benefits-flow/engine/types';

// Award tokens the dependency chain reasons over. These come from two places:
// proto-existing-awards (already held) and the award simulator (marked awarded).
export type AwardToken =
  | 'attendance-allowance'
  | 'pip-daily-living'
  | 'pip-mobility'
  | 'dla-middle-or-high-care'
  | 'dla-higher-rate-mobility'
  | 'adult-disability-payment'
  | 'pension-age-disability-payment';

// Which award tokens a simulated benefit award confers. Marking PIP awarded
// grants both components for the demo, so it unblocks Carer's Allowance (daily
// living) and Blue Badge's automatic route (mobility) at once.
const AWARDED_BENEFIT_TOKENS: Record<string, AwardToken[]> = {
  [B.attendanceAllowance]: ['attendance-allowance'],
  [B.pip]: ['pip-daily-living', 'pip-mobility'],
  [B.adp]: ['adult-disability-payment'],
};

// Build the held-award set from existing-award facts plus simulated awards.
export const heldAwards = (
  facts: Facts,
  awarded: Set<string>,
): Set<AwardToken> => {
  const held = new Set<AwardToken>();
  const fromFact = (key: string, token: AwardToken) => {
    if (facts[key]?.value === true) held.add(token);
  };
  fromFact(M.holdsAttendanceAllowance, 'attendance-allowance');
  fromFact(M.holdsPipDailyLiving, 'pip-daily-living');
  fromFact(M.holdsPipMobility, 'pip-mobility');
  fromFact(M.holdsDlaMiddleOrHighCare, 'dla-middle-or-high-care');
  fromFact(M.holdsDlaHigherRateMobility, 'dla-higher-rate-mobility');
  fromFact(M.holdsAdultDisabilityPayment, 'adult-disability-payment');
  fromFact(
    M.holdsPensionAgeDisabilityPayment,
    'pension-age-disability-payment',
  );
  for (const id of awarded) {
    for (const token of AWARDED_BENEFIT_TOKENS[id] ?? []) held.add(token);
  }
  return held;
};

// Awards that make Carer's Allowance claimable (brief §7.4).
const CARERS_QUALIFYING: AwardToken[] = [
  'attendance-allowance',
  'pip-daily-living',
  'dla-middle-or-high-care',
  'adult-disability-payment',
  'pension-age-disability-payment',
];

export type ResolvedAction = {
  action: ActionState;
  reason?: string;
  route?: 'automatic' | 'discretionary' | 'unknown';
};

// Resolve one benefit's action from its eligibility status, the facts, and the
// held-award set. Only eligible benefits get a meaningful action; ineligible
// benefits keep their base action but the UI keys off status and won't offer it.
export const resolveAction = (
  benefitId: string,
  status: EligibilityStatus,
  held: Set<AwardToken>,
): ResolvedAction => {
  if (status === 'more-info-needed') return { action: 'MORE_INFO_NEEDED' };

  const base = BENEFITS[benefitId]?.baseAction ?? 'GUIDED';
  if (status === 'ineligible') return { action: base };

  // Carer's Allowance — eligible on the carer's own facts, but BLOCKED until the
  // cared-for person holds a qualifying award. This is the whole dependency
  // story: a blocked claim is genuinely eligible, just not yet claimable.
  if (benefitId === B.carersAllowance) {
    const qualifies = CARERS_QUALIFYING.some((t) => held.has(t));
    if (qualifies) {
      return {
        action: 'APPLY_NOW',
        reason:
          'The person you care for holds a qualifying award, so this is claimable now.',
      };
    }
    return {
      action: 'BLOCKED',
      reason:
        "Not yet claimable. The person you care for needs Attendance Allowance or PIP daily living awarded first — we'll come back to this once it lands, usually six to eight weeks after.",
    };
  }

  // Blue Badge — automatic vs discretionary vs check-award (brief §7.4, §8).
  if (benefitId === B.blueBadge) {
    if (held.has('pip-mobility')) {
      return {
        action: 'CHECK_AWARD',
        route: 'unknown',
        reason:
          'You hold PIP — check the award letter for mobility points. 8+ points for moving around, or 10 for planning and following a journey, qualifies for the automatic route. We don’t collect the score, so we can’t tell yet.',
      };
    }
    if (held.has('dla-higher-rate-mobility')) {
      return {
        action: 'GUIDED',
        route: 'automatic',
        reason:
          'Automatic route — DLA higher rate mobility qualifies without an assessment. The council issues the badge.',
      };
    }
    return {
      action: 'GUIDED',
      route: 'discretionary',
      reason:
        'Discretionary route — the council assesses eligibility and may ask for an occupational therapy report. A heavier journey than the automatic route.',
    };
  }

  return { action: base };
};

export type DependencyNote = {
  id: string;
  summary: string;
  // Whether the note's condition is currently met (e.g. AA is awarded).
  active: boolean;
};

// The passporting / severe-disability-addition relationships (brief §7.5) that
// aren't action states but are worth surfacing. Computed from the held set and
// current eligibility.
export const dependencyNotes = (
  held: Set<AwardToken>,
  eligibleIds: Set<string>,
): DependencyNote[] => {
  const notes: DependencyNote[] = [];
  const hasAaOrPip =
    held.has('attendance-allowance') ||
    held.has('pip-daily-living') ||
    held.has('pip-mobility');
  if (eligibleIds.has(B.pensionCredit)) {
    notes.push({
      id: 'pc-severe-disability',
      summary:
        'If Attendance Allowance or PIP is awarded, Pension Credit can gain the severe disability addition.',
      active: hasAaOrPip,
    });
    notes.push({
      id: 'pc-passport',
      summary:
        'A Pension Credit award passports into other entitlements (e.g. Council Tax, NHS costs).',
      active: false,
    });
  }
  return notes;
};
